import { useEffect, useState } from "react";

import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";

import { CameraIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { ILead } from "../../models/Lead";
import useLeads from "../../data/useLeads";
import { IVehicle } from "../../models/Vehicle";
import Card from "../../components/shared/Card/Card";
import { DefaultResponseWithData } from "../../models/system";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import LeadVehiclesTable from "../../components/vehicles/VehicleRow";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import GetAddresses from "../../components/shared/Addresses/GetAddresses";
import Breadcrumbs from "../../components/shared/Breadcrumbs/Breadcrumbs";
import useNotification from "../../components/notifications/useNotifications";


export default function CreateVehicle() {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Vehicle");
  const { setInfo } = usePageNameContext();
  const { vrmLookup } = useVehiclesMutations();
  const { createMultipleCompleted } = useLeads(true, "CreateVehicle");

  const [address, setAddress] = useState<any>();
  const [postcode, setPostcode] = useState("");
  const [addressesOpen, setAddressesOpen] = useState(false);
  const [previousVehicle, setPreviousVehicle] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<DefaultResponseWithData<ILead>[]>([]);
  const [resultsOpen, setResultsOpen] = useState(false);

  const [createVehicleOpen, setCreateVehicleOpen] = useState(false);

  const { register, getValues, setValue, watch, handleSubmit, reset, control } = useForm<any>({
    defaultValues: {
      vehicles: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "vehicles",
  });

  const [isSearching, setIsSearching] = useState(fields.map(() => false));

  useEffect(() => {
    setIsSearching((prev) => {
      if (fields.length === prev.length) return prev;
      if (fields.length > prev.length) return [...prev, false];
      return prev.slice(0, fields.length);
    });
  }, [fields.length]);

  const handleVrmLookup = (index: number, vrm?: string) => {
    setIsSearching((prev) => prev.map((v, i) => (i === index ? true : v)));
    vrm ??= getValues(`vehicles.${index}.vrm`);
    vrmLookup.mutate(vrm ?? "", {
      onSuccess: (data: any) => {
        if (vrm !== undefined) setValue(`vehicles.${index}.vrm`, vrm);
        setValue(`vehicles.${index}.make`, data.data.combinedMake);
        setValue(`vehicles.${index}.model`, data.data.combinedModel);
        addNotification({
          variant: "success",
          primaryText: "Found vehicle",
        });

        if (DateTime.fromISO(data.data.created) < DateTime.now().minus({ minutes: 1 })) {
          setPreviousVehicle(true);
        }

        setIsSearching((prev) => prev.map((v, i) => (i === index ? false : v)));
      },
      onError: (error) => {
        addNotification({
          variant: "error",
          primaryText: "Error looking up VRM",
        });
        setIsSearching((prev) => prev.map((v, i) => (i === index ? false : v)));
      },
    });
  };

  const onSubmit = (data: any) => {
    if (data.vehicles.length === 0) {
      addNotification({
        variant: "error",
        primaryText: "Please add at least one vehicle before creating.",
      });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    let newLead = {
      ...data,
      contact: null,
      vehicles: data.vehicles.map((vehicle: any) => ({
        ...vehicle,
        ...vehicle.address,
      }) as IVehicle)
    };

    newLead.vehicles.forEach((vehicle: IVehicle) => {
      if (vehicle.isDropOff && vehicle.alreadyOnSite) {
        vehicle.collectedOn = new Date().toISOString();
      }
    });

    createMultipleCompleted.mutate(newLead, {
      onSuccess: (data) => {
        // saveHandlers.onSuccess();
        setResults(data);
        setResultsOpen(true);
      },
      onError: (error) => {
        saveHandlers.onError();
      },
      onSettled: () => setIsLoading(false),
    });

  };

  useEffect(() => {
    setInfo({
      name: "New Vehicle",
      desc: "Create new vehicles",
    });
  }, []);

  useEffect(() => {
    //For closing at the end
    if (!resultsOpen && results.length > 0) {
      navigate("/vehicles");
    }
  }, [resultsOpen]);

  const pages = [
    { to: "..", label: "Vehicles", current: false },
    {
      to: "",
      label: "New Vehicle",
      current: true,
    },
  ];

  if (isLoading) {
    return (
      <div className="pt-[20vh] flex justify-center items-center h-full">
        <LoadingWheel />
      </div>
    );
  } else {
    return (
      <>
        <Modal open={resultsOpen} width={"max-w-3xl"} setOpen={setResultsOpen}>
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-lg font-semibold">Vehicles Created</h1>
            {!results.length || results.length === 0 ? (
              <p className="text-sm text-gray-500">No vehicles created.</p>
            ) : (
              <ul className="list-disc pl-5">
                {results.map((result, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="text-sm font-medium">{result.success ? "✅" : "❌"}</span>

                    {result.success && result.data && (
                      <>
                        {result.data.vehicle?.vehicleImageUrl ? (
                          <img
                            className="w-32 h-20 object-contain"
                            src={`${result.data.vehicle?.vehicleImageUrl}`}
                          />
                        ) : (
                          <CameraIcon className="w-14 text-gray-600" />
                        )}
                        <span>{result.data.vehicle?.vrm}</span>
                        <span>{result.data.vehicle?.combinedMake}</span>
                        <span>{result.data.vehicle?.modelRangeDescription}</span>
                        <span>-</span>
                      </>
                    )}

                    <span>{result.message.replace("Lead", "Vehicle")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
        <Modal open={previousVehicle} setOpen={setPreviousVehicle} width="max-w-xl">
          <p>A vehicle with this VRM already exists in the system. </p>
          <br />
          <p>If you proceed, a new vehicle will be created which may lead to duplicate vehicles in the system.</p>
          <div className="mt-4 flex justify-end">
            <ButtonInput label="OK" isSubmit={false} classes="ml-2" onClick={() => setPreviousVehicle(false)} />
          </div>
        </Modal>
        <GetAddresses
          postcode={postcode}
          setAddress={setAddress}
          open={addressesOpen}
          setOpen={setAddressesOpen}
        />
        <Breadcrumbs pages={pages} />

        <Card containerClassName="mt-4">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8 p-4 px-6">
            {/* Vehicle rows section */}
            <div className="">
              <h1 className="text-lg font-semibold mb-2">Vehicles</h1>
              <LeadVehiclesTable
                vehicles={fields}
                register={register}
                setValue={setValue}
                getValues={getValues}
                watch={watch}
                remove={remove}
                handleVrmLookup={handleVrmLookup}
                isSearching={isSearching}
                createVehicleOpen={createVehicleOpen}
                setCreateVehicleOpen={setCreateVehicleOpen}
              />
              <ButtonInput
                label="Add Vehicle"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  append({
                    vrm: "",
                    make: "",
                    model: "",
                    address: {}
                  });
                  setCreateVehicleOpen(true);
                }}
                classes="mt-2"
              />
            </div>

            <div className="mt-6 flex justify-end">
              <ButtonInput
                label="Create Vehicles"
                isSubmit
                classes=""
                onClick={null}
              />
            </div>

          </form>
        </Card>
      </>
    );
  }
} 